import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = { care_provider: '#00A79D', supplier: '#F5871F', sector_org: '#6C8EBF', family: '#D9A441', general: '#475569' };
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

const Breakdown = ({ rows, open, setOpen }) => (
  <div className="space-y-2" data-testid="segments-breakdown">
    {rows.map((r) => (
      <div key={r.key}>
        <button type="button" onClick={() => setOpen(open === r.key ? null : r.key)} data-testid={`segment-row-${r.key}`}
          className="w-full text-left group">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-slate-200"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[r.key] }} />{r.label}</span>
            <span className="text-slate-400" data-testid={`segment-count-${r.key}`}><b className="text-white">{r.visitors.toLocaleString()}</b> · {r.pct}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded mt-1 overflow-hidden">
            <div className="h-full rounded transition-[width] duration-700" style={{ width: `${r.pct}%`, background: COLORS[r.key] }} />
          </div>
        </button>
        {open === r.key && (
          <ul className="mt-2 ml-5 space-y-1 text-xs text-slate-400" data-testid={`segment-pages-${r.key}`}>
            {r.top_pages.length === 0 ? <li>No pages yet.</li> : r.top_pages.map((p) => (
              <li key={p.path} className="flex justify-between gap-3"><span className="truncate">{p.path}</span><span className="shrink-0">{p.views} views</span></li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);

const Trend = ({ months, segments }) => {
  const max = Math.max(1, ...months.map((m) => m.total));
  return (
    <div className="flex items-end gap-3 h-40" data-testid="segments-trend">
      {months.map((m) => (
        <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <span className="text-[11px] text-slate-400">{m.total.toLocaleString()}</span>
          <div className="w-full max-w-[48px] flex flex-col-reverse rounded overflow-hidden" style={{ height: `${(m.total / max) * 110}px` }}>
            {segments.map((s) => m.counts[s.key] > 0 && (
              <div key={s.key} title={`${s.label}: ${m.counts[s.key]}`} style={{ flex: m.counts[s.key], background: COLORS[s.key] }} />
            ))}
          </div>
          <span className="text-[11px] text-slate-500">{monthLabel(m.month)}</span>
        </div>
      ))}
    </div>
  );
};

export const VisitorSegmentsCard = () => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/site-analytics/segments`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  return (
    <Card className="bg-slate-900/50 border-slate-800 mb-6" data-testid="visitor-segments-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2"><Users className="w-4 h-4 text-[#00A79D]" /> Visitor segments</CardTitle>
      </CardHeader>
      <CardContent>
        {!d ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          : d.error ? <p className="text-sm text-slate-500">Could not load visitor segments.</p> : (
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <p className="text-xs text-slate-500 mb-3" data-testid="segments-total">
                  Last 30 days: <b className="text-slate-300">{d.last_30_days.total.toLocaleString()}</b> visitors, {d.last_30_days.self_declared} told us who they are. Others are grouped by the pages they read. Click a segment for its top pages.
                </p>
                <Breakdown rows={d.last_30_days.breakdown} open={open} setOpen={setOpen} />
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-3">Monthly visitors by segment. {d.retention_note}</p>
                <Trend months={d.months} segments={d.segments} />
              </div>
            </div>
          )}
      </CardContent>
    </Card>
  );
};
